import { useRef, useEffect, useCallback, useState } from 'react';

type State = 'paused' | 'ready' | 'loading' | 'error' | 'success';

type Options = {
  url: string;
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  immediate?: boolean;
};

interface AbortError {
  name: 'AbortError';
}

const isAbortError = (x: unknown): x is AbortError => {
  const e = x as AbortError;
  return e?.name !== undefined && e.name === 'AbortError';
};

// a thrown Error, or the JSON body of a response that wasn't ok
export type FetchError = { name?: string; message?: string };

interface DoFetchOpts<B> {
  url?: string;
  body?: B;
}

export const useFetch = <R, B = unknown>(options: Options) => {
  const url = options.url;
  const method = options.method ?? 'GET';
  const immediate = options.immediate ?? (method === 'GET');

  const isMounted = useRef(true);
  useEffect(() => () => {
    isMounted.current = false;
  }, []);

  const [response, setResponse] = useState<R>();
  const [error, setError] = useState<FetchError>();
  const [state, setState] = useState<State>(immediate ? 'ready' : 'paused');

  // a ref, not state: doFetch read it from its closure, which could be stale
  const abortController = useRef<AbortController>();

  const doFetch = useCallback((opts: DoFetchOpts<B>) => {
    const body = opts.body
      ? JSON.stringify(opts.body)
      : undefined;

    // a newer fetch replaces one still running
    abortController.current?.abort();
    const abort = new AbortController();
    abortController.current = abort;

    setState('loading');
    setError(undefined);
    setResponse(undefined);
    let ok = true;

    const fullUrl = opts.url
      ? `${url}/${opts.url}`
      : url;

    return fetch(new Request(fullUrl, {
      method,
      body,
      signal: abort.signal,
      headers: {
        "Content-Type": "application/json",
      },
    }))
    .then((response) => {
      if (!isMounted.current) return;
      ok = response.ok;
      
      return response.json();
    })
    .then((json) => {
      if (isMounted.current) {
        if (ok) {
          setResponse(json);
          setState('success');
          return { ok: true, json };
        } else {
          setError(json);
          setState('error');
          return { ok: false, error: json };
        }
        return { ok: false, error: { message: "not mounted" } };
      }
    })
    .catch((e) => {
      if (isMounted.current && !isAbortError(e)) {
        setError(e);
        setState('error');
        return { ok: false, error: e };
      }
      return { ok: false, error: { message: "not mounted" } };
    });
  }, [url, method]);

  useEffect(() => {
    if (state === 'ready') {
      doFetch({});
    }
  }, [state, doFetch]);

  const refetch = useCallback(() => {
    setState('ready');
  }, []);

  const loading = state === 'loading';

  return [response, error, loading, doFetch, refetch] as const;
};

export const useGet = <D>(url: string) => {
  const [response, error, loading, _doFetch, refetch] = useFetch<D>({ url });

  return [response, error, loading, refetch] as const;
};

export const usePost = <D, B>(url: string) => {
  const [response, error, loading, doFetch] = useFetch<D, B>({ url, method: 'POST' });

  return [response, error, loading, doFetch] as const;
};

export const useDelete = <D>(url: string) => {
  const [response, error, loading, doFetch] = useFetch<D>({ url, method: 'DELETE' });

  return [response, error, loading, doFetch] as const;
};

